import * as React from "react";
import { Phone, Mail, MapPin, Clock, Send, Wrench, Gauge } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const ContactForm = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="28674" className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8 space-y-4 bg-card/50 rounded-lg border border-primary/20 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
          <Send className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground uppercase tracking-tight">Request Received</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 uppercase tracking-wider text-xs font-bold">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" {...field} className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 uppercase tracking-wider text-xs font-bold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 uppercase tracking-wider text-xs font-bold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Your Phone Number" {...field} className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 uppercase tracking-wider text-xs font-bold">Inspection Type</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Wind Mitigation" {...field} className="bg-background border-border focus-visible:border-primary focus-visible:ring-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 uppercase tracking-wider text-xs font-bold">Property Details</FormLabel>
            <FormControl>
              <textarea className={cn("flex min-h-[120px] w-full rounded-md border border-border bg-background px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-1 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-colors resize-y")} placeholder="Please provide the property address and any specific concerns..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-extrabold tracking-widest uppercase h-14 text-base shadow-md hover:shadow-lg transition-all">
        {isSubmitting ? <>
            <Gauge className="mr-2 size-5 animate-spin" />
            PROCESSING...
          </> : "SCHEDULE INSPECTION"}
      </Button>
    </Form>;
};
export default function ReachOutToTheShop() {
  return <section id="schedule-your-inspection" className="bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
      backgroundImage: `radial-gradient(circle at 2px 2px, black 1px, transparent 0)`,
      backgroundSize: '32px 32px'
    }} />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-50" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="space-y-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-extrabold uppercase tracking-widest">
                <Wrench className="size-3" />
                <span>Get Inspected</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-foreground uppercase">
                Schedule Your South Florida <br />
                <span className="text-primary">Home Inspection</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Trusted Inspections provides same-day or next-day scheduling for home, wind mitigation, 4-point, condo, and mold testing inspections. Call, email, or fill out the form below to request a quote or book your inspection today.
              </p>
            </div>

            <div className="grid gap-6">
              {}
              <Link to="tel:786-382-6895" className="group flex items-center gap-6 p-6 rounded-lg border border-border bg-card hover:border-primary/50 hover:shadow-lg transition-all duration-400">
                <div className="size-14 rounded-md bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300 shrink-0">
                  <Phone className="size-7 text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-1">Click to Call</p>
                  <p className="text-3xl md:text-[32px] font-extrabold text-primary transition-colors">Call Trusted Inspections</p>
                </div>
              </Link>

              {}
              <Link to="mailto:info@trusted-fl.com" className="group flex items-center gap-6 p-6 rounded-lg border border-border bg-card hover:border-primary/50 hover:shadow-lg transition-all duration-400">
                <div className="size-14 rounded-md bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300 shrink-0">
                  <Mail className="size-7 text-primary group-hover:text-white transition-colors" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-1">Email Our Office</p>
                  <p className="text-xl md:text-2xl font-bold text-foreground group-hover:text-primary transition-colors break-all">Contact Trusted Inspections</p>
                </div>
              </Link>

              {}
              <div className="flex items-center gap-6 p-6 rounded-lg border border-border bg-card/40">
                <div className="size-14 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                  <MapPin className="size-7 text-secondary" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-1">Service Area</p>
                  <p className="text-lg font-semibold text-foreground">Miami, FL & South Florida Keys</p>
                </div>
              </div>

              {}
              <div className="flex items-center gap-6 p-6 rounded-lg border border-border bg-card/40">
                <div className="size-14 rounded-md bg-secondary/10 flex items-center justify-center shrink-0">
                  <Clock className="size-7 text-secondary" />
                </div>
                <div>
                  <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-1">Office Hours</p>
                  <p className="text-lg font-semibold text-foreground">Mon - Sat: 8:00 AM - 6:00 PM</p>
                  <p className="text-sm text-muted-foreground">Sun: Closed (Emergency Only)</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:mt-10">
            <Card className="border-border bg-card shadow-lg relative overflow-hidden rounded-lg">
              {}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-primary z-20" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-primary z-20" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-primary z-20" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-primary z-20" />

              <CardHeader className="pb-4 border-b border-border/50 bg-muted/30">
                <CardTitle className="text-2xl font-extrabold uppercase tracking-tight flex items-center gap-3">
                  <span className="w-1.5 h-6 bg-primary rounded-sm inline-block" />
                  Schedule Your Inspection
                </CardTitle>
                <CardDescription className="pl-4.5 pt-1 text-muted-foreground">
                  Fill out the details below for a quote or to request a booking date.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-6 md:p-8">
                <FormProvider formId="schedule_inspection_form" sectionName="schedule-your-inspection" defaultValues={{
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true
                }
              }} submitText="SCHEDULE INSPECTION" successMessage="Inspection request received. Our team will contact you within 24 hours to confirm your booking." errorMessage="Transmission failed. Please check your connection or call us directly.">
                  <p className="text-sm text-muted-foreground mb-6">
                    Tell us the property address, inspection type, and preferred date, and we will follow up to confirm availability.
                  </p>
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}